/**
 * Training SOP — Operations / Shipping & Receiving track.
 *
 * The only track whose audience works ACROSS jobs rather than within one. They
 * live on the two company-wide pages (Receiving and Equipment) and rarely open
 * a project workspace at all, so the track is organised by those pages rather
 * than by the job lifecycle.
 *
 * ACCESS: receiving writes need `receiving.edit` (held by ReceivingEditor and
 * ProjectEditor); equipment writes need `equipment.edit`. The EquipmentManager
 * role carries `projects.view_all` so its holders can see every job. Exactly
 * which of these any individual holds varies, so capability notes point at the
 * symptom ("the button isn't there") rather than asserting a role. A request's
 * RENTAL TERMS — its rate and its files (#571) — follow the same rule as the
 * edit drawer: `equipment.edit` (plus Admin), so the topic on them is gated on
 * that permission too.
 *
 * ⚠️ The single biggest Ops-facing gotcha is the activity lock: a job still on
 * a SYSTAC-/ClosedWon- placeholder REFUSES new receiving orders and equipment
 * requests. To Ops that looks like the app is broken, when it's actually the
 * estimator owing a job number. It leads the troubleshooting section.
 *
 * Verified against the code (2026-07-25 sweep).
 */
import { type Track } from '../types';

export const OPS_TRACK: Track = {
  id: 'ops',
  label: 'Operations / Shipping & Receiving',
  audience:
    'The shipping & receiving desk and equipment coordinators. You work across every job rather than inside one, so your two screens are Receiving and Equipment.',
  /**
   * ⚠️ BY ROLE, NOT BY PERMISSION, AND IT MATTERS WHICH.
   *
   * This track had no `suggestedFor` at all, on the reasoning above that
   * "exactly which of these any individual holds varies". That was survivable
   * while the field was only a badge, and stopped being so when the admin
   * roll-up started taking its DENOMINATOR from `suggestedTrack`: with no rule
   * to match, dedicated ops staff fell through to foundations alone, their
   * Operations ticks stopped counting, and they could read 100% having read
   * eleven topics (Codex, #351).
   *
   * `receiving.edit` and `equipment.edit` are the wrong test even though they
   * are what the screens check. ProjectEditor carries BOTH — deliberately, so
   * PMs can submit their own orders — so a permission rule would sweep up every
   * PM. It would also lose to the PM track's rule anyway, both being
   * permission-tier and PM being declared first. The roles below identify the
   * people whose JOB is these two screens, which is what the suggestion is for.
   *
   * ⚠️ `EquipmentManager` is an admin-created role matched by LABEL (its id
   * varies per environment; see 2026-06-11_equipment-manager-view-all-projects
   * .sql). A rename in Admin ▸ Roles silently stops this matching, and the
   * failure looks like "Foundations only" reappearing in the roll-up, not like
   * an error. `ReceivingEditor` is a system role (id 9) and is safe.
   *
   * ⚠️ ✅ VERIFIED EMPTY ON PROD, NOT ENFORCED. A role rule outranks a
   * permission rule, so anyone holding ReceivingEditor or EquipmentManager *as
   * well as* a PM's `projects.edit` would be suggested Operations over Project
   * Manager. The prod user list was checked on 2026-08-18 and nobody holds
   * that combination, which is why this ships as-is.
   *
   * Nothing prevents it from arising later, though — the pairing is legal, and
   * granting it is two clicks in Admin ▸ Roles. It would show up as a PM
   * reading "Operations / Shipping & Receiving" in the Track counted column
   * rather than as an error, so that column is the place it surfaces.
   */
  suggestedFor: { anyRole: ['ReceivingEditor', 'EquipmentManager'] },
  sections: [
    // ── 1. Receiving ────────────────────────────────────────────────────────
    {
      id: 'ops-receiving',
      title: 'Shipping & Receiving',
      intro:
        'Every inbound order for every job, in one list. Replaces the old pre-order form.',
      topics: [
        {
          id: 'ops-new-order',
          title: 'Logging an order',
          summary:
            'Record what was ordered, from whom, when it is expected, and how to track it. The job can be picked from the list or just typed in if the job is not set up yet.',
          steps: [
            'Open **Receiving** from the top navigation and use **+ New Order**.',
            'Fill in the item, vendor, quantity, and the job it is for.',
            'Set the delivery method, order date and expected delivery.',
            'Add the carrier and tracking number — the tracking link builds itself.',
          ],
          watchOut: [
            'The carrier list covers UPS, FedEx, Amazon, USPS and ArcBest. For a freight carrier, choose **Other…** and type the name.',
            'An order does not have to be attached to a job. If the job is not in the Project Tracker yet, type the job name in the free-text field and the order groups under "No Project".',
          ],
          deepLink: '/receiving',
          access: { anyPermission: ['receiving.edit'] },
        },
        {
          id: 'ops-work-the-list',
          title: 'Working the list',
          summary:
            'Orders group by job and sort by whichever column you need. Finished orders drop out of sight so the list stays about what is still coming.',
          steps: [
            'Search by item, vendor, tracking number, job number or project name.',
            'Filter by status or by project.',
            'Check **Include closed** to bring completed orders back into view.',
            'Click a row to open its details; **Edit** to change it.',
          ],
          watchOut: [
            'Closed orders are **hidden by default** — the badge tells you how many are being held back. If an order has "disappeared", it has almost certainly just closed.',
          ],
          deepLink: '/receiving',
          access: { anyPermission: ['receiving.edit'] },
        },
      ],
    },

    // ── 2. Equipment ────────────────────────────────────────────────────────
    {
      id: 'ops-equipment',
      title: 'Equipment',
      intro:
        'Rental and owned equipment across every job — requests, what is on rent, and getting it back off site.',
      topics: [
        {
          id: 'ops-equipment-list',
          title: 'What is out, and where',
          summary:
            'The Equipment page lists every request with its status, vendor, dates and the job it is on. The tiles at the top count what is active, split between rental and owned.',
          steps: [
            'Open **Equipment** from the top navigation.',
            'Search by equipment, type, vendor, job number or project.',
            'Sort by Status to see the list in workflow order rather than alphabetically — Requested, Ordered, On-site, Called Off, Complete, Canceled.',
          ],
          watchOut: [
            'Complete and Canceled records are **hidden by default**. Check **Include completed** to see them.',
            'The Rental and Owned tiles count only **active** items; the Total tile includes finished ones. They are not meant to add up.',
          ],
          deepLink: '/equipment',
        },
        {
          id: 'ops-owned-units',
          title: 'Assigning an owned unit',
          summary:
            'When you send out company equipment rather than a rental, record which unit went. The unit numbers come from a maintained list, so you are picking rather than typing from memory.',
          steps: [
            'Open the request and edit it.',
            'Choose the unit from the **Owned Equipment #** list.',
          ],
          watchOut: [
            'The list only offers units that are currently in service. A unit that has been retired disappears from the list but stays recorded on jobs it was already assigned to.',
            'You cannot add a new unit to that list yourself — the catalog is managed by an admin. Ask them to add it and it will appear in your list right away.',
          ],
          access: { anyPermission: ['equipment.edit'] },
        },
        {
          // #571. Gated on exactly what the app checks (canSeeRentalTerms in
          // rentalTermsAccess.ts): equipment.edit, which allows() also grants
          // Admin. Since 2026-10-12 that includes the PMs and project engineers,
          // not only equipment managers.
          id: 'ops-rental-agreement',
          title: 'Keeping the rental agreement with the request',
          summary:
            'Attach the signed rental agreement, and any other paperwork, to the equipment request itself. The files are kept in the Project Tracker, not in the job\'s SharePoint Job Folder, and only people who can edit equipment requests (equipment managers, PMs and project engineers) and admins can see them or the rental rate.',
          steps: [
            'The **+ New Request** form has no place for files, so create the request first. Then click it on **Equipment** to open its details, and use **Edit**.',
            'Under **Rental Agreement & Files**, drag the agreement from your computer onto the box, or click the box to choose it: a PDF; a Word, Excel or PowerPoint file; a PNG, JPEG, GIF or WebP image; or a text or CSV file, up to 15 MB each. You can drop several at once.',
            'Click a file\'s name to download it, or the **×** beside it to remove it. Each file shows who added it and when.',
            'Set the **Rate** under **Vendor & Billing**. To use the rate sheet\'s price instead, pick the model in the **Equipment Type** search, even if one is already shown, with a **Billing Cycle** chosen: an **Apply … rate** button appears under Equipment Type when the sheet has a price for that cycle. Then **Save**.',
          ],
          watchOut: [
            'A file is added the moment you drop or pick it and removed the moment you confirm. Neither waits for **Save**, and closing the drawer does not undo them.',
            'If you drop several files and one is too big or empty, the rest still go in, and the box tells you which one did not.',
            'Field leaders, estimators and view-only users never see the rate or these files. Anyone who can edit the request can **Delete** the request, which takes its files with it. The Project Tracker has no undo for a delete.',
          ],
          deepLink: '/equipment',
          access: { anyPermission: ['equipment.edit'] },
        },
        {
          id: 'ops-call-off',
          title: 'Calling equipment off site',
          summary:
            'When a piece is finished on a job, calling it off records everything the vendor needs for collection. Only available once the item is On-site.',
          steps: [
            'Find the item and use **Call Off**.',
            'Enter when it is ready for pickup, the pickup location, and a site contact name and phone.',
            'Record the condition — Clean, Needs cleaning, or Damage.',
          ],
          watchOut: [
            'Pickup location and site contact are **required**, but they pre-fill from the job, so usually you are confirming rather than typing.',
            'If you record Damage you must describe it. That description is what protects the company in the vendor conversation.',
          ],
          access: { anyPermission: ['equipment.edit'] },
        },
        {
          id: 'ops-extend',
          title: 'Extending time on site',
          summary:
            'A job runs long and the equipment needs to stay. Extend records the new end date and why.',
          steps: [
            'Find the item and use **Extend** — available while it is On-site or already Called Off.',
            'Set the new end date and give a reason.',
          ],
          watchOut: [
            'The new date must be later than the current one. Both the date and the reason are required.',
          ],
          access: { anyPermission: ['equipment.edit'] },
        },
        {
          id: 'ops-rate-calculator',
          title: 'Checking what a rental will cost',
          summary:
            'Estimate a rental before committing to it. Pick the equipment and a number of days and it compares daily, weekly and monthly pricing, flagging the cheapest.',
          steps: [
            'On **Equipment**, open the **Rate Calculator**.',
            'Choose the equipment and enter the number of days.',
          ],
          watchOut: [
            'It assumes **7 days a week and 28 days a month**, and rounds up to whole billing periods. A 30-day rental prices as two months.',
            'These are estimates for comparison, **not quotes**. Confirm the real price with the vendor.',
          ],
          deepLink: '/equipment',
        },
      ],
    },

    // ── 3. When something won't go through ──────────────────────────────────
    {
      id: 'ops-troubleshooting',
      title: 'When something won\'t go through',
      topics: [
        {
          id: 'ops-locked-job',
          title: '"It won\'t let me add an order for this job"',
          summary:
            'The most common one, and it is not your problem to fix. A job that has not yet been given its real Sage Intacct number refuses new receiving orders and equipment requests.',
          steps: [
            'Look at the job number. Does it start with **SYSTAC-** or **ClosedWon-**?',
            'If so, the job is still a placeholder and is locked for this kind of work.',
            'Chase the **estimator** — they set the job up in Sage and enter the real number. Everything unlocks the moment they do.',
          ],
          watchOut: [
            'In the meantime you can still log the order against the free-text job name and attach it to the job properly later.',
          ],
        },
        {
          id: 'ops-no-buttons',
          title: '"I can\'t see the New or Edit buttons"',
          summary:
            'Receiving and Equipment are separately granted. It is normal to have one and not the other.',
          steps: ['Ask an admin to add the access you are missing — receiving, equipment, or both.'],
        },
        {
          id: 'ops-order-vanished',
          title: '"An order has disappeared from the list"',
          summary:
            'Almost always a filter rather than a deletion. Both screens hide finished work by default.',
          steps: [
            'On Receiving, check **Include closed**.',
            'On Equipment, check **Include completed**.',
          ],
        },
      ],
    },
  ],
};
